import React, { Fragment, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import "./Banner.less";

import { ZomentumRoutes } from "@/Contracts";
import { BannerNotificationMessageType } from "@/Contracts/Others/NotificationFeed";
import { ActivityMessageType } from "@/Contracts/Others/RecentActivityFeed";

import BannerNotificationActionCreators from "@/Actions/BannerNotification";
import { isDocumentClientView } from "@/Contracts/Utils";
import { useLocation, useRouteMatch, useHistory } from "react-router-dom";
import { GlobalState } from "@/Reducers/interface";
import { ZTypography, ZIcon } from "@atoms";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { EIcons } from "@atoms/ZIcon/interface";
import moment from "moment";
import { EColors } from "@atoms/ZColors/interface";
import { getBeamerPost } from "@/Services/Others/BannerNotificationService";
import { BeamerNameConstants } from "./interface";

const Banner = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const history = useHistory();
	const { isOpen, isLoading, bannerFeedMessage, userData } = useSelector(
		(state: GlobalState) => ({
			...state.bannerNotification,
			userData: state.user.userData
		})
	);

	const isEndClientView = isDocumentClientView(location.pathname);
	const isPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView)?.isExact;

	const beamerMutationObserver = useRef<MutationObserver | null>();

	const getDOMElements = (): Record<string, HTMLElement> => {
		const appNavBar = document.getElementsByClassName(
			"app-sider ant-layout-sider"
		)[0] as HTMLElement;

		const navMenuSideBar = document.getElementsByClassName(
			"settings-sider"
		)[0] as HTMLElement;

		const rightDrawer = document.getElementsByClassName(
			"ant-drawer-right"
		)[0] as HTMLElement;

		const bannerWrap = document.getElementsByClassName(
			"banner-card-wrap"
		)[0] as HTMLElement;

		const beamerBarTopActive = document.getElementsByClassName(
			"beamerAnnouncementBarTopActive"
		)[0] as HTMLElement;

		const beamerBarTop = document.getElementsByClassName(
			"beamerAnnouncementBarTop"
		)[0] as HTMLElement;

		return {
			appNavBar,
			navMenuSideBar,
			bannerWrap,
			beamerBarTopActive,
			beamerBarTop,
			rightDrawer
		};
	};

	const observeAddedNodes = (addedNodes: NodeList) => {
		// We need to check for beamer divs being added to DOM by the third-party script
		addedNodes.forEach(node => {
			const addedNode = node as HTMLElement;
			if (!!!addedNode.tagName) return;

			const isBeamer =
				addedNode?.id === BeamerNameConstants.BEAMER_ANNOUNCEMENT_BAR;

			const { bannerWrap, beamerBarTopActive } = getDOMElements();
			const isNotificationBannerPresent =
				bannerWrap?.classList?.contains?.("show");

			// Adjust right drawers if beamer/zomentum banner is being shown.
			const isDrawerOpened =
				addedNode?.classList?.contains?.("ant-drawer-right");

			const isBeamerOrNotificationBannerActive =
				!!beamerBarTopActive || isNotificationBannerPresent;

			if (isBeamerOrNotificationBannerActive && isDrawerOpened) {
				addedNode.style.top = "40px";
				addedNode.style.height = "calc(100% - 40px)";
			}

			if (isBeamer) getLatestBeamerBanner(isNotificationBannerPresent);
		});
	};

	const observeRemovedNodes = (removedNodes: NodeList) => {
		// We need to check for beamer's div being removed.
		removedNodes.forEach(node => {
			const removedNode = node as HTMLElement;

			if (
				removedNode.id === BeamerNameConstants.BEAMER_ANNOUNCEMENT_BAR
			) {
				// Reset the applied styles
				document.documentElement.style.paddingTop = "0px";

				const { appNavBar, navMenuSideBar, rightDrawer } =
					getDOMElements();

				if (navMenuSideBar && appNavBar) {
					appNavBar.style.marginTop = "4.5rem";
					appNavBar.style.minHeight = "100vh";

					navMenuSideBar.style.height = "calc(100vh - 4.5rem)";
				}

				if (rightDrawer) {
					rightDrawer.style.top = "0px";
					rightDrawer.style.height = "100%";
				}

				// Disconnect the observer
				beamerMutationObserver.current?.disconnect();
			}
		});
	};

	const mutationsObserverCallback = (mutations: MutationRecord[]) => {
		mutations.forEach(mutation => {
			observeRemovedNodes(mutation.removedNodes);
			observeAddedNodes(mutation.addedNodes);
		});
	};

	useEffect(() => {
		beamerMutationObserver.current = new MutationObserver(
			mutationsObserverCallback
		);

		beamerMutationObserver.current.observe(document.body, {
			childList: true,
			subtree: true
		});
	}, []);

	if (isEndClientView || isPdfView) {
		return null;
	}

	const showBeamer = () => {
		const { beamerBarTop, beamerBarTopActive, appNavBar, navMenuSideBar } =
			getDOMElements();

		// Set appropriate styles for sidebar, navmenu and beamer.
		if (beamerBarTop && beamerBarTopActive) {
			if (appNavBar && navMenuSideBar) {
				appNavBar.style.marginTop = "7.5rem";
				appNavBar.style.minHeight = "calc(100vh - 40px)";
				navMenuSideBar.style.height = "calc(100vh - 4.5rem - 40px)";
			}

			beamerBarTopActive.style.paddingTop = "40px";
			beamerBarTop.style.cssText = "display: block !important";

			const longTextClass = beamerBarTop.querySelector(
				".longText"
			) as HTMLElement;
			if (longTextClass) longTextClass.classList?.remove("longText");
		}
	};

	const getLatestBeamerBanner = async (
		isNotificationBannerPresent?: boolean
	) => {
		try {
			const beamerPosts = await getBeamerPost(userData);
			// NOTE: Beamer API takes few retries to return the top bar announcement.
			const topBarAnnouncement = beamerPosts[0]?.boostedAnnouncement
				? beamerPosts[0]
				: undefined;

			if (topBarAnnouncement) {
				const isUserCreationOlderThanPost = moment(
					userData.created
				).isBefore(topBarAnnouncement.date);

				// Show the top bar if the user creation is older than the post
				if (isUserCreationOlderThanPost && !isNotificationBannerPresent)
					showBeamer();
			}
		} catch (error) {
			console.error(error);
		}
	};

	const severity =
		(bannerFeedMessage && bannerFeedMessage.banner_severity) || "";

	const dismissNotification = async () => {
		const { beamerElem, rightDrawer } = getDOMElements();

		if (rightDrawer) {
			rightDrawer.style.top = "0px";
			rightDrawer.style.height = "100%";
		}

		// Check for beamer post dismissal of zomentum banner.
		if (beamerElem) await getLatestBeamerBanner();

		dispatch(BannerNotificationActionCreators.dismissBanner());
	};

	const handleNotificationLinkClick = (
		messageType: BannerNotificationMessageType | ActivityMessageType,
		value?: string
	) => {
		if (messageType === ActivityMessageType.REDIRECT && !!value?.length) {
			history.replace(value);
		}
	};

	return (
		<div className={`banner-card-wrap${isOpen ? " show" : ""}`}>
			<div className={`banner-card ${severity}`}>
				{!isLoading ? (
					<Fragment>
						{!!bannerFeedMessage?.message?.length && (
							<ZIcon
								color={EColors.GOLD}
								colorVariant={6}
								name={EIcons.WARNINGCIRCLE}
								size="sm"
							/>
						)}
						{bannerFeedMessage?.message?.map(
							({ message_type, text, value }, index) => {
								return message_type ===
									BannerNotificationMessageType.TEXT ? (
									<ZTypography.ZText
										weight={EFontWeights.MEDIUM}
										key={`banner-text-${index}`}
										className="text-1"
									>
										{text}
									</ZTypography.ZText>
								) : (
									<ZTypography.ZLink
										color={EColors.GEEKBLUE}
										colorVariant={6}
										weight={EFontWeights.MEDIUM}
										className="text-1"
										onClick={() =>
											handleNotificationLinkClick(
												message_type,
												value
											)
										}
										key={`banner-text-link-${index}`}
									>
										{text}
									</ZTypography.ZLink>
								);
							}
						)}
						{bannerFeedMessage?.is_dismissable &&
							!!bannerFeedMessage?.message?.length && (
								<ZTypography.ZText
									weight={EFontWeights.MEDIUM}
									onClick={dismissNotification}
									className="close-icon text-1"
								>
									Remind Later
								</ZTypography.ZText>
							)}
					</Fragment>
				) : (
					<span>Loading...</span>
				)}
			</div>
		</div>
	);
};

export default Banner;
